import type { GrantEntry } from "../../shared/rbac/roles";
import { grantKey, principalKey } from "../../shared/rbac/validation";

export type GrantRemoval = {
  userGroupId?: string;
  userId?: string;
  serviceAccountId?: string;
  roleIds?: string[];
};

// The current grants come straight from a synced roleGrants query, whose
// columns are nullable; only the principal + role fields are read.
type CurrentGrant = {
  userGroupId?: string | null;
  userId?: string | null;
  serviceAccountId?: string | null;
  builtinRoleId?: string | null;
  customRoleId?: string | null;
};

// The client-side inverse of the server's resolveGrantDelta: turns the UI's
// "full desired grant list" into the add/remove deltas patchScopeGrants takes.
// add is every desired grant not already present; remove drops a whole
// principal when it's gone from the desired set, or just the roles it lost.
// Principal-generic — works for any shareable scope's grants.
export function deriveScopeGrantDelta(
  current: readonly CurrentGrant[],
  desired: readonly GrantEntry[]
): { add: GrantEntry[]; remove: GrantRemoval[] } {
  const currentKeys = new Set(current.map(grantKey));

  const seenAdd = new Set<string>();
  const add: GrantEntry[] = [];
  for (const g of desired) {
    const key = grantKey(g);
    if (currentKeys.has(key) || seenAdd.has(key)) continue;
    seenAdd.add(key);
    add.push(g);
  }

  const desiredRolesByPrincipal = new Map<string, Set<string>>();
  for (const g of desired) {
    const p = principalKey(g);
    const role = g.builtinRoleId ?? g.customRoleId;
    if (!p || !role) continue;
    (desiredRolesByPrincipal.get(p) ?? addSet(desiredRolesByPrincipal, p)).add(
      role
    );
  }

  const remove: GrantRemoval[] = [];
  const handled = new Set<string>();
  for (const g of current) {
    const p = principalKey(g);
    if (!p || handled.has(p)) continue;
    handled.add(p);

    const desiredRoles = desiredRolesByPrincipal.get(p);
    const principal = {
      userGroupId: g.userGroupId ?? undefined,
      userId: g.userId ?? undefined,
      serviceAccountId: g.serviceAccountId ?? undefined,
    };
    if (!desiredRoles) {
      // Principal is gone from the desired set entirely.
      remove.push(principal);
      continue;
    }
    const lostRoles = current
      .filter((c) => principalKey(c) === p)
      .map((c) => c.builtinRoleId ?? c.customRoleId)
      .filter((role): role is string => !!role && !desiredRoles.has(role));
    if (lostRoles.length > 0) {
      remove.push({ ...principal, roleIds: [...new Set(lostRoles)] });
    }
  }

  for (const g of add) {
    const p = principalKey(g);
    if (p && remove.some((r) => principalKey(r) === p)) {
      throw new Error(
        `deriveScopeGrantDelta: principal ${p} is in both add and remove`
      );
    }
  }

  return { add, remove };
}

function addSet(map: Map<string, Set<string>>, key: string): Set<string> {
  const set = new Set<string>();
  map.set(key, set);
  return set;
}
